import { useEffect, useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertBanner,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  FormField,
} from "@lendcord/concord";
import { PasswordInput } from "@/auth/components/PasswordInput";
import { useChangePasswordMutation } from "@/auth/api/auth.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { PASSWORD_REGEX } from "@/auth/validators";

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const intl = useIntl();
  const [changePassword] = useChangePasswordMutation();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{
    oldPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Keyed on `open` (not routed through the Dialog's onOpenChange callback)
  // so fields clear no matter how the dialog closes — Escape, the overlay,
  // the X button, or the parent toggling the `open` prop directly.
  useEffect(() => {
    if (!open) {
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({});
      setFormError(undefined);
    }
  }, [open]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const nextErrors: typeof errors = {};
    if (!oldPassword) {
      nextErrors.oldPassword = intl.formatMessage({ id: "lender.changePasswordDialog.oldPasswordError" });
    }
    if (!PASSWORD_REGEX.test(newPassword)) {
      nextErrors.newPassword = intl.formatMessage({ id: "lender.changePasswordDialog.newPasswordError" });
    }
    if (confirmPassword !== newPassword) {
      nextErrors.confirmPassword = intl.formatMessage({ id: "lender.changePasswordDialog.confirmPasswordError" });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setFormError(undefined);
    setIsSubmitting(true);
    try {
      await changePassword({ oldPassword, newPassword }).unwrap();
      onOpenChange(false);
    } catch (error) {
      if (isQueryApiError(error) && error.code === "LEND-072") {
        setErrors({ oldPassword: intl.formatMessage({ id: "lender.changePasswordDialog.wrongOldPasswordError" }) });
      } else {
        setFormError(intl.formatMessage({ id: "lender.changePasswordDialog.genericError" }));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-center">
            <FormattedMessage id="lender.changePasswordDialog.heading" />
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
          {formError && <AlertBanner status="error">{formError}</AlertBanner>}

          <FormField
            label={intl.formatMessage({ id: "lender.changePasswordDialog.oldPasswordLabel" })}
            error={errors.oldPassword}
            required
          >
            <PasswordInput
              value={oldPassword}
              onChange={setOldPassword}
              placeholder={intl.formatMessage({ id: "lender.changePasswordDialog.oldPasswordPlaceholder" })}
              autoComplete="current-password"
            />
          </FormField>

          <FormField
            label={intl.formatMessage({ id: "lender.changePasswordDialog.newPasswordLabel" })}
            error={errors.newPassword}
            required
            hint={
              errors.newPassword
                ? undefined
                : intl.formatMessage({ id: "lender.changePasswordDialog.newPasswordHint" })
            }
          >
            <PasswordInput
              value={newPassword}
              onChange={setNewPassword}
              placeholder={intl.formatMessage({ id: "lender.changePasswordDialog.newPasswordPlaceholder" })}
              autoComplete="new-password"
            />
          </FormField>

          <FormField
            label={intl.formatMessage({ id: "lender.changePasswordDialog.confirmPasswordLabel" })}
            error={errors.confirmPassword}
            required
          >
            <PasswordInput
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder={intl.formatMessage({ id: "lender.changePasswordDialog.confirmPasswordPlaceholder" })}
              autoComplete="new-password"
              disablePaste
            />
          </FormField>

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <FormattedMessage id="lender.changePasswordDialog.submittingButton" />
            ) : (
              <FormattedMessage id="lender.changePasswordDialog.submitButton" />
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
